import { useCallback, useState } from "react";

import type { OutgoingIntakeFormNode } from "@/api/types/outgoingIntake";
import type { Step } from "@/features/requestsIde/editor/stepModel";

import { builderSections } from "../model";

export type BuilderSelection = {
  /** The question open for editing in the card, if any. */
  openId: string | null;
  setOpenId: (id: string | null) => void;
  /** Sections folded shut, by section key. */
  collapsed: ReadonlySet<string>;
  toggleSection: (key: string) => void;
  /** Open a question, unfold its section and scroll it into view. */
  jumpTo: (nodeId: string) => void;
};

const scrollToRow = (nodeId: string) =>
  requestAnimationFrame(() =>
    document
      .querySelector<HTMLElement>(`[data-builder-row="${CSS.escape(nodeId)}"]`)
      ?.scrollIntoView({ block: "center", behavior: "smooth" })
  );

/** What's open and folded in a `QuestionsCard`, shared with its rail. */
export const useBuilderSelection = (
  schema: OutgoingIntakeFormNode[]
): BuilderSelection => {
  const [openId, setOpenId] = useState<string | null>(null);
  const [collapsed, setCollapsed] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const toggleSection = useCallback((key: string) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const jumpTo = useCallback(
    (nodeId: string) => {
      const owns = (s: Step) =>
        s.node.node_id === nodeId ||
        (s.node.children ?? []).some((c) => c.node_id === nodeId);
      const section = builderSections(schema).find((s) => s.steps.some(owns));
      const id = section?.steps.find(owns)?.node.node_id ?? nodeId;
      const key = section?.id;
      setCollapsed((prev) => {
        if (!key || !prev.has(key)) return prev;
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
      setOpenId(id);
      scrollToRow(id);
    },
    [schema]
  );

  return { openId, setOpenId, collapsed, toggleSection, jumpTo };
};
